import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { downloadFile, arrayBufferToBase64 } from 'utils/helpers';
import moment from 'moment-mini';
import { take } from 'rxjs/operators';
import { ComputedFrom } from '@certemy/common';
import { isIEBrowser, isSafariBrowser, isAndroidChromeBrowser } from '../../../utils/browser-helper';
import { PrintService } from '../../../services/print/print.service';
import { ReportGenerateService } from '../report-generate.service';
import { REPORT_OPTION, REPORT_DATE_FORMAT } from './report-print-export-options.constants';
import { GenerateReportOption, ReportPrintOptions } from '../report-generate.interface';
import { REPORT_ENTITIES } from 'constants/constants';

@Component({
  selector: 'cm-report-print-export-options',
  templateUrl: './report-print-export-options.component.html',
  styleUrls: ['./report-print-export-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportPrintExportOptionsComponent {
  @Input() reportQuery: GenerateReportOption;
  @Input() fileName: string;
  @Input() entityName: string;
  @Input() disabled = false;
  @Input() options: ReportPrintOptions[];

  @ComputedFrom('entityName')
  get defaultOptions() {
    switch (this.entityName) {
      case REPORT_ENTITIES.TESTING_WORKFLOW_QUALIFICATION_DASHBOARD:
      case REPORT_ENTITIES.TESTING_WORKFLOW_CANDIDATES_DASHBOARD:
      case REPORT_ENTITIES.TESTING_WORKFLOW_SCORE_SUMMARY:
      case REPORT_ENTITIES.TESTING_WORKFLOW_SCORE_DETAIL:
        return [
          {
            id: REPORT_OPTION.EXPORT_XLS,
            title: `Export as XLS`,
            icon: 'export',
          },
        ];
      default:
        return [
          {
            id: REPORT_OPTION.PRINT,
            title: `Print ${this.entityName}`,
            icon: 'print',
          },
          {
            id: REPORT_OPTION.EXPORT,
            title: `Export ${this.entityName}`,
            icon: 'export',
          },
        ];
    }
  }

  constructor(private reportGenerateService: ReportGenerateService, private printService: PrintService) {}

  private printInIEAndSafari() {
    this.reportGenerateService
      .getGeneratedReportHtml(this.reportQuery)
      .pipe(take(1))
      .subscribe((html: string) => this.printService.printPDF(html));
  }

  private downloadPDF(file: ArrayBuffer) {
    const date = moment().format(REPORT_DATE_FORMAT);
    const fileName = `${this.fileName}_${date}.pdf`.replace(/\s/g, '_');

    downloadFile(file, fileName, 'application/pdf');
  }

  private downloadXLS(file: ArrayBuffer) {
    const date = moment().format(REPORT_DATE_FORMAT);
    const fileName = `${this.fileName}_${date}.xlsx`.replace(/\s/g, '_');

    downloadFile(file, fileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }

  onOptionSelect(option: number) {
    if ((isIEBrowser() || isSafariBrowser() || isAndroidChromeBrowser()) && option === REPORT_OPTION.PRINT) {
      this.printInIEAndSafari();

      return;
    }

    this.reportGenerateService
      .getGeneratedReportBuffer(this.reportQuery, option)
      .pipe(take(1))
      .subscribe((file: ArrayBuffer) => {
        const optionHandleMap = {
          [REPORT_OPTION.PRINT]: () => this.printService.printPDF(arrayBufferToBase64(file)),
          [REPORT_OPTION.EXPORT]: () => this.downloadPDF(file),
          [REPORT_OPTION.EXPORT_XLS]: () => this.downloadXLS(file),
        };

        optionHandleMap[option]();
      });
  }
}
